import { Company } from './../../../models/swagger/bff/company';
import { MaritalStatus } from './../../../models/swagger/bff/maritalStatus';
import { inject, Injectable } from '@angular/core';
import { ApiService, DefaultHttpHeaders, HeadersEnum, HttpInputParametersType } from '../api.service';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { delay, finalize, switchMap, take, tap } from 'rxjs/operators';
import { Mocks } from '@core/services/mocks/mocks';
import { select, Store } from '@ngrx/store';
import { HttpHeaders } from '@angular/common/http';
import { getInitFlow } from '@store/flow-data/init-flow';
import { InitFlow, ProductLine, ProductLineType, Quote } from '@core/models/swagger';
import {
  CancellationReason,
  ContactType,
  Deductible,
  Gender,
  GetOrderRequest,
  GetOrderResponse,
  InsuranceReason,
  MultiQuoteRequest,
  MultiQuoteResponse,
  OccupationKind,
  OrderRequest,
  OrderResponse,
  PartnerRelationshipKind,
  PaymentFrequency,
  PaymentMethod,
  PersonTitle,
  PriceKind,
  PrivateLiabilityDeductible,
  PrivateLiabilityPackage,
  PrivateLiabilityTariff,
  SearchPartnerRequest,
  SearchPartnerResponse,
  UsageKind,
} from '@core/models/swagger/bff';
import dayjs from 'dayjs';
import { getSelectedProductLine } from '@store/flow-data/general';
import { QueryParamsStore } from '@core/services/queryparams-store/queryparams-store.service';

@Injectable({
  providedIn: 'root',
})
export class GeneralService extends ApiService {
  mocked = false;

  constructor(private readonly queryparams_store: QueryParamsStore) {
    super();
  }

  private readonly loadingSubjects: Record<string, BehaviorSubject<boolean>> = {
    postOrder: new BehaviorSubject<boolean>(false),
    postGetOrder: new BehaviorSubject<boolean>(false),
    postMultiQuote: new BehaviorSubject<boolean>(false),
    postDocument: new BehaviorSubject<boolean>(false),
    postPartnerSearch: new BehaviorSubject<boolean>(false),
  };

  readonly postOrderLoading$ = this.loadingSubjects.postOrder.asObservable();
  readonly postPartnerSearchLoading$ = this.loadingSubjects.postPartnerSearch.asObservable();
  readonly postGetOrderLoading$ = this.loadingSubjects.postGetOrder.asObservable();
  readonly postMultiQuoteLoading$ = this.loadingSubjects.postMultiQuote.asObservable();
  readonly postDocumentLoading$ = this.loadingSubjects.postDocument.asObservable();

  partnerSearch: SearchPartnerResponse;

  private executePostRequest<TRequest, TResponse>({
    endpoint,
    body,
    requestKey,
    mocked,
    headers = {},
  }: {
    endpoint: string;
    body: TRequest;
    requestKey: string;
    mocked?: boolean;
    headers?: DefaultHttpHeaders;
  }): Observable<TResponse> {
    this.loadingSubjects[requestKey].next(true);

    if (this.mocked || mocked) {
      return Mocks[requestKey]().pipe(
        delay(2000),
        finalize(() => this.loadingSubjects[requestKey].next(false))
      );
    }

    const correlationKey = HeadersEnum.X_CORRELATION_ID;

    if (headers[correlationKey]) {
      return this.post<TRequest, TResponse>({ suffix: endpoint, body, headers }).pipe(
        finalize(() => this.loadingSubjects[requestKey].next(false))
      );
    }

    return this.store.pipe(
      select(getInitFlow),
      take(1),
      switchMap((initFlow: InitFlow) => {
        const enrichedHeaders = {
          ...headers,
          [correlationKey]: initFlow?.correlationId?.toString(),
        };
        return this.post<TRequest, TResponse>({ suffix: endpoint, body, headers: enrichedHeaders });
      }),
      finalize(() => this.loadingSubjects[requestKey].next(false))
    );
  }

  private executeGetRequest<TResponse>({
    endpoint,
    queryParams,
    requestKey,
    mocked,
    headers,
  }: {
    endpoint: string;
    queryParams?: HttpInputParametersType;
    requestKey: string;
    mocked?: boolean;
    headers?: DefaultHttpHeaders;
  }): Observable<TResponse> {
    this.loadingSubjects[requestKey].next(true);

    if (this.mocked || mocked) {
      return Mocks[requestKey]().pipe(
        delay(2000),
        finalize(() => this.loadingSubjects[requestKey].next(false))
      );
    }

    this.store.pipe(select(getInitFlow), take(1)).subscribe((initFlow: InitFlow) => {
      headers = {
        ...headers,
        [HeadersEnum.X_CORRELATION_ID]: initFlow?.correlationId?.toString(),
        [HeadersEnum.X_API_KEY]: '13',
        // [HeadersEnum.X_CORRELATION_ID]: '3ae21614-f357-46a8-bdcf-d1d0ef15c6f9',
        [HeadersEnum.ACCEPT_LANGUAGE]: 'de',
      };
    });

    return this.get<TResponse>({
      suffix: endpoint,
      queryParams,
      headers,
    }).pipe(finalize(() => this.loadingSubjects[requestKey].next(false)));
  }

  postMultiQuote(quote: Quote): Observable<MultiQuoteResponse> {
    return this.executePostRequest<MultiQuoteRequest, MultiQuoteResponse>({
      endpoint: '/multiQuote',
      body: this.mapQuoteToMultiQuoteRequest(quote),
      requestKey: 'postMultiQuote',
      mocked: false,
    });
  }

  postGetOrder(quote: Quote): Observable<GetOrderResponse> {
    return this.executePostRequest<GetOrderRequest, GetOrderResponse>({
      endpoint: '/getOrder',
      body: this.mapQuoteToOrderRequest(quote),
      requestKey: 'postGetOrder',
      mocked: false,
    });
  }

  postOrder(quote: Quote): Observable<OrderResponse> {
    return this.executePostRequest<OrderRequest, OrderResponse>({
      endpoint: '/order',
      body: this.mapQuoteToOrderRequest(quote),
      requestKey: 'postOrder',
      mocked: false,
    });
  }

  postPartnerSearch(quote: Quote): Observable<SearchPartnerResponse> {
    return this.executePostRequest<SearchPartnerRequest, SearchPartnerResponse>({
      endpoint: '/partner/search',
      body: this.mapQuoteToSearchPartnerRequest(quote),
      requestKey: 'postPartnerSearch',
      mocked: false,
    });
  }

  postDocument(body: DocumentBody, headers?: DefaultHttpHeaders): Observable<DocumentResponse> {
    return this.executePostRequest<DocumentBody, DocumentResponse>({
      endpoint: '/document',
      body,
      requestKey: 'postDocument',
      headers,
      mocked: true,
    });
  }

  private mapQuoteToSearchPartnerRequest(quote: Quote): SearchPartnerRequest {
    return {
      firstName: quote.policyholderData.name,
      lastName: quote.policyholderData.surname,
      dateOfBirth: quote.policyholderData.birthdate,
      postCode: quote.policyholderData.address.postCode,
      email: quote.policyholderData.contact.email,
    };
  }

  private mapQuoteToMultiQuoteRequest(quote: Quote): MultiQuoteRequest {
    let newInitFlow: InitFlow;
    this.store.pipe(select(getInitFlow), take(1)).subscribe((initFlow: InitFlow) => {
      newInitFlow = initFlow;
    });

    const { verVU, verVM, parVM, vorVM, antVM } = this.queryparams_store.snapshot();

    return {
      quote: {
        quoteTime: dayjs(new Date()).toISOString(),
        insuranceReason: InsuranceReason.InsurerChange,
        marketing: {
          campaignCode: newInitFlow.campaignInfo.campaignCode,
        },
        partners: [
          {
            partnerType: 'Person',
            partnerId: 'PA-01',
            ...(parVM ? { partnerNumberExternal: parVM } : {}),
            contacts: [
              {
                contactType: ContactType.EMail,
                usageKind: UsageKind.Private,
                email: quote.policyholderData?.contact?.email,
                preferredContact: true,
              },
              {
                contactType: ContactType.PhoneMobile,
                usageKind: UsageKind.Private,
                phone: quote.policyholderData?.contact?.phone,
                preferredContact: false,
              },
            ],
            partnerAddresses: [
              {
                street: quote.policyholderData?.address?.street,
                houseNumber: quote.policyholderData?.address?.houseNumber,
                postCode: quote.policyholderData?.address?.postCode ?? '14513',
                city: quote.policyholderData?.address?.city,
                countryCode: 'DE',
                // TODO: Default preferred postal address?
                preferredPostalAddress: true,
              },
            ],
            bankDetails: [
              quote.bankAccountOwnerData?.ibanRef !== undefined
                ? {
                    IBANRef: quote.bankAccountOwnerData.ibanRef,
                    debitAuthorization: true,
                  }
                : {
                    IBAN: quote.bankAccountOwnerData?.iban?.replace(/\s+/g, ''),
                    debitAuthorization: true,
                  },
            ],
            // TODO: Do we need to define a relationship between bank account owner and policyholder?
            partnerRelationships: [
              quote.insuranceData?.insuranceType === PrivateLiabilityTariff.Family
                ? {
                    partnerId: 'Partner',
                    // TODO: Default relationship kind for partner?
                    relationshipKind: PartnerRelationshipKind.Spouse,
                  }
                : undefined,
            ],
            gender: (quote.policyholderData?.gender?.parameterId === 'None'
              ? 'Divers'
              : quote.policyholderData?.gender?.parameterId) as Gender,
            title: quote.policyholderData?.title?.parameterId as PersonTitle,
            firstName: quote.policyholderData?.name,
            lastName: quote.policyholderData?.surname,
            dateOfBirth: quote.policyholderData?.birthdate,
            employments: [
              {
                publicService: quote.policyholderData?.publicSector,
                occupationKind: 'Employee',
              },
            ],
            household: {
              numberOfChildren: 0,
            },
            maritalStatus: 'Single',
          },
        ],
        policyholder: {
          partnerId: 'PA-01',
        },
        insuredObjects: [],
        salesProduct: {
          paymentMethod: PaymentMethod.Debit,
          paymentFrequency: PaymentFrequency.Yearly,
          products: [
            {
              productType: 'PrivateLiability',
              tariff: quote.insuranceData?.insuranceType,
              insurancePeriod: {
                begin: quote.insuranceData?.insuranceBeginDate,
              },
              conditions: [
                {
                  // TODO: Check if the incentive code is the promo code
                  incentiveCode: quote.insuranceData?.promotionCode,
                },
              ],
              elementaryProducts: [
                {
                  productType: 'PrivateLiability',
                  deductible:
                    quote.insuranceData?.deductible === undefined
                      ? PrivateLiabilityDeductible.None
                      : quote.insuranceData.deductible.parameterId === 'value_0'
                        ? 'None'
                        : (String(quote.insuranceData?.deductible?.parameterId).replace(
                            'value_',
                            ''
                          ) as PrivateLiabilityDeductible),
                },
              ],
            },
          ],
        },
        agent: {
          // TODO: Default values?
          agentNumber: verVU ?? '00-WEB',
          // TODO: Default values?
          agentNumberExternal: verVM ?? '00',
        },
        ...(vorVM ? { transactionNumberExternal: vorVM } : {}),
        ...(antVM ? { applicationNumberExternal: antVM } : {}),
      },
      quoteControl: {
        calculateRegularPrices: true,
        calculateReferencePrices: true,
      },
    };
  }

  private mapQuoteToGetOrderRequest(quote: Quote): GetOrderRequest {
    let newInitFlow: InitFlow;
    this.store.pipe(select(getInitFlow), take(1)).subscribe((initFlow: InitFlow) => {
      newInitFlow = initFlow;
    });

    return {
      order: {
        quoteTime: dayjs(new Date()).toISOString(),
        insuranceReason: InsuranceReason.FirstInsurance,
        marketing: {
          campaignCode: newInitFlow.campaignInfo.campaignCode,
        },
        partners: [
          {
            partnerType: 'Person',
            partnerId: 'Policyholder',
            contacts: [
              {
                contactType: ContactType.EMail,
                usageKind: UsageKind.Private,
                email: quote.policyholderData?.contact?.email,
                preferredContact: true,
              },
              {
                contactType: ContactType.PhoneMobile,
                usageKind: UsageKind.Private,
                phone: quote.policyholderData?.contact?.phone,
                preferredContact: false,
              },
            ],
            partnerAddresses: [
              {
                street: quote.policyholderData?.address?.street,
                houseNumber: quote.policyholderData?.address?.houseNumber,
                postCode: quote.policyholderData?.address?.postCode ?? '14513',
                city: quote.policyholderData?.address?.city,
                countryCode: 'DE',
                // TODO: Default preferred postal address?
                preferredPostalAddress: true,
              },
            ],
            bankDetails: [
              quote.policyholderData?.iban !== undefined
                ? {
                    IBAN: quote.policyholderData?.iban,
                    debitAuthorization: quote.policyholderData?.debitAuthorization,
                  }
                : undefined,
            ],
            // TODO: Do we need to define a relationship between bank account owner and policyholder?
            partnerRelationships: [
              quote.insuranceData?.insuranceType === PrivateLiabilityTariff.Family
                ? {
                    partnerId: 'Partner',
                    // TODO: Default relationship kind for partner?
                    relationshipKind: PartnerRelationshipKind.Spouse,
                  }
                : undefined,
            ],
            gender: (quote.policyholderData?.gender?.parameterId === 'None'
              ? 'Divers'
              : quote.policyholderData?.gender?.parameterId) as Gender,
            title: quote.policyholderData?.title?.parameterId as PersonTitle,
            firstName: quote.policyholderData?.name,
            lastName: quote.policyholderData?.surname,
            dateOfBirth: quote.policyholderData?.birthdate,
            maritalStatus: quote.policyholderData?.maritalStatus?.parameterId as MaritalStatus,
            employments: [
              {
                publicService: quote.policyholderData?.publicSector,
              },
            ],
          },
        ],
        policyholder: {
          partnerId: 'Policyholder',
        },
        insuredObjects: [],
        salesProduct: {
          paymentMethod: PaymentMethod.Debit,
          paymentFrequency: PaymentFrequency.Yearly,
          products: [
            {
              productType: 'PrivateLiability',
              tariff: quote.insuranceData?.insuranceType,
              insurancePeriod: {
                begin: quote.insuranceData?.insuranceBeginDate,
              },
              conditions: [
                {
                  // TODO: Check if the incentive code is the promo code
                  incentiveCode: quote.insuranceData?.promotionCode,
                },
              ],
              elementaryProducts: [
                {
                  productType: 'PrivateLiability',
                  deductible:
                    quote.insuranceData?.deductible === undefined
                      ? PrivateLiabilityDeductible.None
                      : (String(quote.insuranceData?.deductible?.parameterId).replace(
                          'value_',
                          ''
                        ) as PrivateLiabilityDeductible),
                },
              ],
            },
          ],
        },
        agent: {
          // TODO: Default values?
          agentNumber: 'Verti',
          // TODO: Default values?
          agentNumberExternal: '00',
        },
      },
    };
  }

  private mapQuoteToOrderRequest(quote: Quote): OrderRequest {
    let newInitFlow: InitFlow;
    let newGeneral: ProductLine;
    this.store.pipe(select(getInitFlow), take(1)).subscribe((initFlow: InitFlow) => {
      newInitFlow = initFlow;
    });

    this.store.pipe(select(getSelectedProductLine), take(1)).subscribe((general: ProductLine) => {
      newGeneral = general;
    });

    const isDifferentPayer = quote.bankAccountOwnerData?.otherPerson;
    const isCompanyPayer = !quote.bankAccountOwnerData?.isPerson;
    const policyholderHasPartner = quote.partnerData !== undefined && quote.partnerData !== null;

    const { verVU, verVM, parVM, vorVM, antVM } = this.queryparams_store.snapshot();

    return {
      order: {
        quoteTime: dayjs(new Date()).toISOString(),
        insuranceReason: InsuranceReason.InsurerChange,
        marketing: {
          campaignCode: newInitFlow.campaignInfo.campaignCode,
          taf: quote?.externalReferences ?? undefined,
        },
        partners: [
          {
            partnerType: 'Person',
            partnerId: 'PA-01',
            ...(parVM ? { partnerNumberExternal: parVM } : {}),
            contacts: [
              {
                contactType: ContactType.EMail,
                usageKind: UsageKind.Private,
                email: quote.policyholderData?.contact?.email,
                preferredContact: true,
              },
              {
                contactType: ContactType.PhoneMobile,
                usageKind: UsageKind.Private,
                phone: quote.policyholderData?.contact?.phone,
                preferredContact: false,
              },
            ],
            partnerAddresses: [
              {
                street: quote.policyholderData?.address?.street,
                houseNumber: quote.policyholderData?.address?.houseNumber,
                postCode: quote.policyholderData?.address?.postCode ?? '14513',
                city: quote.policyholderData?.address?.city,
                countryCode: 'DE',
                // TODO: Default preferred postal address?
                preferredPostalAddress: true,
              },
            ],
            bankDetails: !isDifferentPayer
              ? [
                  quote.bankAccountOwnerData.ibanRef !== undefined
                    ? {
                        IBANRef: quote.bankAccountOwnerData.ibanRef,
                        debitAuthorization: true,
                      }
                    : {
                        IBAN: quote.bankAccountOwnerData?.iban?.replace(/\s+/g, ''),
                        debitAuthorization: true,
                      },
                ]
              : [],
            partnerRelationships: [
              policyholderHasPartner
                ? {
                    partnerId: 'PA-02',
                    relationshipKind: PartnerRelationshipKind.Spouse,
                  }
                : undefined,
            ],
            gender: (quote.policyholderData?.gender?.parameterId === 'None'
              ? 'Divers'
              : quote.policyholderData?.gender?.parameterId) as Gender,
            title: quote.policyholderData?.title?.parameterId as PersonTitle,
            firstName: quote.policyholderData?.name,
            lastName: quote.policyholderData?.surname,
            dateOfBirth: quote.policyholderData?.birthdate,
            maritalStatus: quote.policyholderData?.maritalStatus?.parameterId as MaritalStatus,
            inputTaxDeduction: quote.policyholderData?.taxDeduction,
            employments: [
              {
                occupationKind: quote.policyholderData.occupation.parameterId as OccupationKind,
                publicService: quote.policyholderData?.publicSector,
              },
            ],
            household: {
              numberOfChildren: quote.policyholderData?.maritalStatus?.parameterId === 'Family' ? 1 : 0,
            },
          },
          ...(policyholderHasPartner
            ? [
                {
                  partnerType: 'Person',
                  partnerId: 'PA-02',
                  partnerAddresses: [
                    {
                      street: quote.policyholderData?.address?.street,
                      houseNumber: quote.policyholderData?.address?.houseNumber,
                      postCode: quote.policyholderData?.address?.postCode ?? '14513',
                      city: quote.policyholderData?.address?.city,
                      countryCode: 'DE',
                      preferredPostalAddress: true,
                    },
                  ],
                  firstName: quote.partnerData?.name,
                  lastName: quote.partnerData?.surname,
                  gender: (quote.partnerData?.gender?.parameterId === 'None'
                    ? 'Divers'
                    : quote.partnerData?.gender?.parameterId) as Gender,
                  title: quote.partnerData?.title?.parameterId as PersonTitle,
                },
              ]
            : []),
          ...(isDifferentPayer
            ? isCompanyPayer
              ? [
                  {
                    partnerType: 'LegalPerson',
                    partnerId: 'PA-03',
                    partnerAddresses: [
                      {
                        street: quote.bankAccountOwnerData?.address?.street,
                        houseNumber: quote.bankAccountOwnerData?.address?.houseNumber,
                        postCode: quote.bankAccountOwnerData?.address?.postCode ?? '14513',
                        city: quote.bankAccountOwnerData?.address?.city,
                        countryCode: 'DE',
                        // TODO: Default preferred postal address?
                        preferredPostalAddress: true,
                      },
                    ],
                    companyName: quote.bankAccountOwnerData.name,
                    bankDetails: [
                      quote.bankAccountOwnerData.ibanRef !== undefined
                        ? {
                            IBANRef: quote.bankAccountOwnerData.ibanRef,
                            debitAuthorization: true,
                          }
                        : {
                            IBAN: quote.bankAccountOwnerData?.iban?.replace(/\s+/g, ''),
                            debitAuthorization: true,
                          },
                    ],
                  },
                ]
              : [
                  {
                    partnerType: 'Person',
                    partnerId: 'PA-03',
                    partnerAddresses: [
                      {
                        street: quote.bankAccountOwnerData?.address?.street,
                        houseNumber: quote.bankAccountOwnerData?.address?.houseNumber,
                        postCode: quote.bankAccountOwnerData?.address?.postCode ?? '14513',
                        city: quote.bankAccountOwnerData?.address?.city,
                        countryCode: 'DE',
                        // TODO: Default preferred postal address?
                        preferredPostalAddress: true,
                      },
                    ],
                    firstName: quote.bankAccountOwnerData?.name,
                    lastName: quote.bankAccountOwnerData?.surname,
                    gender: (quote.bankAccountOwnerData?.gender?.parameterId === 'None'
                      ? 'Divers'
                      : quote.bankAccountOwnerData?.gender?.parameterId) as Gender,
                    title: quote.bankAccountOwnerData?.title?.parameterId as PersonTitle,
                    bankDetails: [
                      quote.bankAccountOwnerData.ibanRef !== undefined
                        ? {
                            IBANRef: quote.bankAccountOwnerData.ibanRef,
                            debitAuthorization: true,
                          }
                        : {
                            IBAN: quote.bankAccountOwnerData?.iban?.replace(/\s+/g, ''),
                            debitAuthorization: true,
                          },
                    ],
                  },
                ]
            : []),
        ],
        policyholder: {
          partnerId: 'PA-01',
        },
        insuredObjects: [],
        salesProduct: {
          paymentMethod: PaymentMethod.Debit,
          paymentFrequency: PaymentFrequency.Yearly,
          ...(isDifferentPayer
            ? {
                paymentContributor: {
                  partnerId: 'PA-03',
                },
              }
            : {}),
          products: [
            {
              productType: 'PrivateLiability',
              tariff: quote.insuranceData?.insuranceType,
              package: newGeneral.productLine.id as PrivateLiabilityPackage,
              insurancePeriod: {
                begin: quote.insuranceData?.insuranceBeginDate,
              },
              conditions: [
                {
                  // TODO: Check if the incentive code is the promo code
                  incentiveCode: quote.insuranceData?.promotionCode,
                },
              ],
              elementaryProducts: [
                {
                  productType: 'PrivateLiability',
                  deductible:
                    quote.insuranceData?.deductible === undefined
                      ? PrivateLiabilityDeductible.None
                      : quote.insuranceData.deductible.parameterId === 'value_0'
                        ? 'None'
                        : (String(quote.insuranceData?.deductible?.parameterId).replace(
                            'value_',
                            ''
                          ) as PrivateLiabilityDeductible),
                  coInsuredPersons: [...(policyholderHasPartner ? [{ partnerId: 'PA-02' }] : [])],
                },
              ],
            },
          ],
        },
        ...(quote.previousInsuranceData?.previousInsurance
          ? {
              previousInsurances: [
                {
                  company: quote.previousInsuranceData.insurer.parameterId as Company,
                  cancellationReason: quote.previousInsuranceData.canceller.parameterId as CancellationReason,
                  products: [
                    {
                      productType: 'PrivateLiability',
                      elementaryProducts: [
                        {
                          damages: {
                            numberOfDamages: damagesMap[quote.previousInsuranceData.claims.parameterId],
                            numberOfYears: 2,
                          },
                          productType: 'type',
                        },
                      ],
                    },
                  ],
                },
              ],
            }
          : {}),
        agent: {
          // TODO: Default values?
          agentNumber: verVU ?? '00-WEB',
          // TODO: Default values?
          agentNumberExternal: verVM ?? '00',
        },
        ...(vorVM ? { transactionNumberExternal: vorVM } : {}),
        ...(antVM ? { applicationNumberExternal: antVM } : {}),
      },
    };
  }
}

const damagesMap: Record<string, number> = {
  None: 0,
  One: 1,
  Two: 2,
  '3orMore': 3,
};
interface DocumentBody {
  base64Encoded: boolean;
  campaignCode: string;
  quote: Quote;
  documentKind: DocumentKind;
}

export interface DocumentResponse {
  documentContent: any;
}

export const DocumentKind = {
  ALL_IN_ONE: 'AllInOne',
  CONTRACT: 'Contract',
  CONTRACT_CONDITIONS: 'ContractConditions',
} as const;
export type DocumentKind = (typeof DocumentKind)[keyof typeof DocumentKind];
